import React from "react";
import { X } from "lucide-react";
import { useDndContext } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../../../components/ui/button";
import { ServiceCatalogItem } from "../../../store/ServiceCatalog/types";
import { useCatalogsStore } from "../../../store/Catalogs";
import { useCentralStore } from "../../../store/Central";
import { useTemplatesStore } from "../../../store/Templates/templates.store";
import { getItemDisplayInfo } from "../utils";

interface ServiceCatalogItemRowProps {
	item: ServiceCatalogItem;
	folderId: string;
	index: number;
	depth: number;
	onRemove: () => void;
}

export const ServiceCatalogItemRow: React.FC<ServiceCatalogItemRowProps> = ({
	item,
	folderId,
	index,
	depth,
	onRemove,
}) => {
	const tariffsLookup = useCatalogsStore((s) => s.tariffsLookup);
	const articles = useCentralStore((s) => s.articles);
	const templatesLookup = useTemplatesStore((s) => s.templatesLookup);

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
		isOver,
	} = useSortable({
		id: `item-${folderId}-${index}`,
		data: { type: "item", parentId: folderId, index },
	});

	const { active } = useDndContext();
	const showDropIndicator = isOver && !!active;

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	const info = getItemDisplayInfo(
		item,
		tariffsLookup,
		articles,
		templatesLookup
	);

	const indicatorLeft = depth * 20 + 24;

	if (!info) {
		return (
			<div ref={setNodeRef} style={style} className="relative">
				{showDropIndicator && (
					<div
						className="absolute top-0 right-0 h-0.5 bg-blue-500 z-10"
						style={{ left: indicatorLeft }}
					/>
				)}
				<div
					{...attributes}
					{...listeners}
					style={{ paddingLeft: indicatorLeft }}
					className="flex items-center gap-2 py-1 px-1 opacity-40 group cursor-grab"
				>
					<span className="text-xs bg-gray-200 text-gray-500 rounded px-1.5 py-0.5 font-mono">
						?
					</span>
					<span className="text-xs text-muted-foreground italic flex-1">
						Nicht gefunden
					</span>
					<Button
						variant="ghost"
						size="icon"
						className="h-5 w-5 opacity-0 group-hover:opacity-100"
						onClick={onRemove}
						aria-label="Position entfernen"
					>
						<X className="h-3 w-3" />
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div ref={setNodeRef} style={style} className="relative">
			{showDropIndicator && (
				<div
					className="absolute top-0 right-0 h-0.5 bg-blue-500 z-10"
					style={{ left: indicatorLeft }}
				/>
			)}
			<div
				{...attributes}
				{...listeners}
				style={{ paddingLeft: indicatorLeft }}
				className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/50 group transition-colors cursor-grab"
				data-testid={`catalog-item-${info.type}-${info.code}`}
			>
				<span
					className={`text-xs rounded px-1.5 py-0.5 font-mono font-semibold shrink-0 ${info.badgeClass}`}
				>
					{info.badge}
				</span>
				<span className="text-xs font-mono text-muted-foreground shrink-0">
					{info.code}
				</span>
				<span
					className="text-sm truncate flex-1 min-w-0"
					title={info.description}
				>
					{info.description}
				</span>
				<Button
					variant="ghost"
					size="icon"
					className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
					onClick={onRemove}
					aria-label="Position aus Ordner entfernen"
					data-testid="catalog-item-remove"
				>
					<X className="h-3 w-3" />
				</Button>
			</div>
		</div>
	);
};
